import { z } from 'zod';
import { defineStepType } from '../contract.js';
/**
 * The person chooses one option about their own health, and says why when the choice calls for it.
 *
 * WHY A TYPE AND NOT A QUESTION ON ANOTHER FORM. `acknowledgment` records agreement to fixed
 * words; `info-task` tells somebody something; `document-upload` proves a file arrived. None of
 * them records a CHOICE, which is why the seed for `onboarding-hepb-info-sheet-issued` says the
 * info-sheet half exists and *"the choice itself (`hepb-offer-and-response`) has no step type
 * yet"*. Influenza (06b form 6) is the same shape, so this type is built once and serves both.
 *
 * THE REASON VOCABULARY IS CLOSED, AND THAT IS A SAFETY PROPERTY, not a simplification. The
 * design warns the candidate not to include medical details; a free-text box invites exactly
 * that, and whatever is typed there becomes employee-health data in a field nothing treats as
 * sensitive. So a reason is a key from the definition's own list. A definition that wants an
 * "another reason" option declares it as an option; it does not open a text field.
 *
 * NO SIGNATURE. IC 5.19.1 requires no declination form for influenza, and the design states the
 * flu choice is sent without one. A choice that must be signed (Hepatitis B's waiver) needs the
 * signature half as its own step, for the same reason the info sheet is its own step: one
 * evidentiary claim per instance.
 */
export const healthChoiceOptionSchema = z
    .object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(200),
    /** When true, this option is not complete until a reason from `reasons` is given. */
    requiresReason: z.boolean().default(false),
})
    .strict();
export const healthChoiceReasonSchema = z
    .object({ key: z.string().min(1).max(64), label: z.string().min(1).max(200) })
    .strict();
export const healthChoiceConfigSchema = z
    .object({
    heading: z.string().min(1),
    /** "Why we ask", in the candidate's words. */
    intro: z.string().min(1),
    /** The policy this comes from, shown as a link. Both or neither. */
    policyLabel: z.string().min(1).nullable().default(null),
    policyUrl: z.string().url().nullable().default(null),
    statusPrompt: z.string().min(1).default('Choose one'),
    choices: z.array(healthChoiceOptionSchema).min(2).max(10),
    reasonPrompt: z.string().min(1).default('Tell us why'),
    /** The closed list a reason is drawn from. Empty is legal when no choice requires one. */
    reasons: z.array(healthChoiceReasonSchema).max(20).default([]),
    requirementKey: z.string().min(1).nullable().default(null),
})
    .strict()
    .superRefine((config, ctx) => {
    const duplicate = (keys) => new Set(keys).size !== keys.length;
    if (duplicate(config.choices.map((choice) => choice.key))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['choices'], message: 'Choice keys must be unique' });
    }
    if (duplicate(config.reasons.map((reason) => reason.key))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reasons'], message: 'Reason keys must be unique' });
    }
    /*
     * A choice that asks for a reason with no list to draw one from can never be completed, and
     * the person would see a prompt with nothing under it. Refused at publish rather than
     * discovered by a candidate.
     */
    if (config.choices.some((choice) => choice.requiresReason) && config.reasons.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reasons'], message: 'A choice asks for a reason, so list the reasons to choose from' });
    }
    if ((config.policyLabel === null) !== (config.policyUrl === null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['policyUrl'], message: 'Give the policy a label and a link, or neither' });
    }
});
export const healthChoiceSubmissionSchema = z
    .object({
    choice: z.string().min(1).max(64),
    /** Null, never absent: "no reason given" and "this choice takes no reason" read the same on the wire. */
    reason: z.string().min(1).max(64).nullable().default(null),
})
    .strict();
/**
 * The one place the config and an answer are compared, so `readyToSubmit`, `checkSubmission` and
 * `sections` cannot disagree about whether an answer is finished.
 */
export function checkHealthChoice(config, submission) {
    const chosen = config.choices.find((choice) => choice.key === submission.choice);
    if (chosen === undefined)
        return { ok: false, message: 'That choice is not one of the options on this form.' };
    if (chosen.requiresReason) {
        if (submission.reason === null)
            return { ok: false, message: 'Choose a reason.' };
        if (!config.reasons.some((reason) => reason.key === submission.reason)) {
            return { ok: false, message: 'That reason is not one of the options on this form.' };
        }
        return { ok: true };
    }
    /*
     * REFUSED, not ignored. A reason attached to a choice that does not take one is data nobody
     * asked for, kept on an employee-health record — and silently dropping it would leave the
     * candidate believing they had said something the record does not hold.
     */
    if (submission.reason !== null)
        return { ok: false, message: 'This choice does not take a reason.' };
    return { ok: true };
}
function healthChoiceSections(config, value) {
    // A draft is never validated (ADR-013 §3), so read it defensively.
    const draft = healthChoiceSubmissionSchema.partial().safeParse(value);
    const choice = draft.success ? (draft.data.choice ?? null) : null;
    const reason = draft.success ? (draft.data.reason ?? null) : null;
    const chosen = config.choices.find((option) => option.key === choice);
    const sections = [
        { key: 'status', label: config.statusPrompt, state: chosen === undefined ? 'not-started' : 'done' },
    ];
    // The reason section exists only when the chosen option asks for one, so "1 of 2" does not
    // appear beside a choice that has nothing more to answer.
    if (chosen?.requiresReason === true) {
        sections.push({ key: 'reason', label: config.reasonPrompt, state: reason === null ? 'not-started' : 'done' });
    }
    return sections;
}
export const healthChoice = defineStepType({
    type: 'health-choice',
    submittedBy: 'person',
    label: 'Health choice',
    description: 'Records the person\'s own choice about a health offer, with a reason where the choice calls for one.',
    configSchema: healthChoiceConfigSchema,
    submissionSchema: healthChoiceSubmissionSchema,
    readyToSubmit: ({ config, draft }) => {
        const parsed = healthChoiceSubmissionSchema.safeParse(draft);
        return parsed.success && checkHealthChoice(config, parsed.data).ok;
    },
    sections: ({ config, value }) => healthChoiceSections(config, value),
    /**
     * No `store-file`: nothing is produced to file. The evidence is the answer itself, which lives
     * on the instance and is read from there.
     */
    onApprove: ({ instance, config }) => [
        ...(config.requirementKey !== null
            ? [
                {
                    kind: 'mint-credential',
                    personId: instance.personId,
                    requirementKey: config.requirementKey,
                    identifier: null,
                    issuedAt: null,
                    /*
                     * Null, deliberately. A seasonal requirement's expiry is the SEASON's, computed by
                     * the expiry engine from the catalog row — not a date this step invents. Supplying
                     * one here would pin the renewal to the day the form was approved.
                     */
                    expiresAt: null,
                },
            ]
            : []),
        { kind: 'recompute-rollup', personId: instance.personId },
    ],
    // Async to match the contract; the check itself needs nothing awaited.
    checkSubmission: async ({ config, submission }) => checkHealthChoice(config, submission),
});
//# sourceMappingURL=health-choice.js.map